import { motion } from "framer-motion";
import { Compass, Users, MessageCircle, ArrowDown, Instagram, Mail } from "lucide-react";
import { ParallaxSection } from "@/components/landing/ParallaxSection";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { LANDING } from "@/constants/testIds";

const SERVICES = [
  { icon: Compass, title: "Paket Tour", text: "Perjalanan wisata dengan rencana perjalanan yang tersusun rapi dari awal hingga akhir." },
  { icon: Users, title: "Perjalanan Grup", text: "Berangkat bersama keluarga, komunitas, atau rekan kerja dengan pendampingan tim kami." },
  { icon: MessageCircle, title: "Konsultasi Perjalanan", text: "Tanyakan destinasi, jadwal, dan dokumen perjalanan langsung kepada tim CS kami." },
];

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

const Nav = () => (
  <header className="absolute inset-x-0 top-0 z-20">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6 sm:px-10">
      <div className="rounded-md bg-white/95 px-3 py-2"><img src="/logo.png" alt="Arumichi Travel" className="h-8 w-auto" /></div>
      <nav className="hidden gap-8 text-sm text-white/90 sm:flex">
        <a href="#layanan" className="hover:text-white" data-testid={LANDING.navServices}>Layanan</a>
        <a href="#kontak" className="hover:text-white" data-testid={LANDING.navContact}>Kontak</a>
      </nav>
    </div>
  </header>
);

const Hero = () => (
  <ParallaxSection image="/landing/hero.jpg" className="h-screen min-h-[600px]" overlay="bg-gradient-to-b from-slate-900/50 via-slate-900/30 to-slate-900/70" speed={20} testid={LANDING.hero}>
    <Nav />
    <div className="mx-auto flex h-full max-w-6xl flex-col justify-end px-6 pb-24 sm:px-10">
      <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.3em] text-sky-200">Arumichi Travel · Surakarta</motion.p>
      <motion.h1 {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="font-heading mt-5 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
        Jelajahi dunia, kami yang mengatur perjalanannya.
      </motion.h1>
      <motion.a {...fadeUp} transition={{ duration: 0.7, delay: 0.2 }} href="#layanan" className="mt-10 inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-slate-900 hover:bg-sky-50 transition-colors" data-testid={LANDING.heroCta}>
        Lihat layanan <ArrowDown className="h-4 w-4" />
      </motion.a>
    </div>
  </ParallaxSection>
);

const Services = () => (
  <section id="layanan" className="bg-white py-24" data-testid={LANDING.services}>
    <div className="mx-auto max-w-6xl px-6 sm:px-10">
      <motion.p {...fadeUp} className="overline-label text-brand">Layanan Kami</motion.p>
      <motion.h2 {...fadeUp} className="font-heading mt-3 max-w-xl text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Perjalanan yang tertata, tanpa repot.</motion.h2>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {SERVICES.map((s, i) => (
          <motion.div key={s.title} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="rounded-lg border border-slate-200 p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
            <s.icon className="h-7 w-7 text-brand" />
            <h3 className="font-heading mt-6 text-lg font-semibold text-slate-900">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Quote = () => (
  <ParallaxSection image="/landing/city.jpg" className="h-[70vh] min-h-[420px]" overlay="bg-slate-950/55" testid={LANDING.band}>
    <div className="mx-auto flex h-full max-w-6xl items-center px-6 sm:px-10">
      <motion.p {...fadeUp} className="font-heading max-w-2xl text-3xl font-semibold leading-tight tracking-tight text-white sm:text-5xl">Setiap kota punya cerita. Temukan cerita Anda berikutnya.</motion.p>
    </div>
  </ParallaxSection>
);

const Contact = () => (
  <ParallaxSection image="/landing/group.jpg" className="h-[75vh] min-h-[460px]" overlay="bg-slate-900/60" testid={LANDING.contactBand}>
    <div className="mx-auto flex h-full max-w-6xl flex-col items-start justify-center px-6 sm:px-10">
      <motion.h2 {...fadeUp} className="font-heading max-w-xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">Siap merencanakan perjalanan?</motion.h2>
      <motion.p {...fadeUp} className="mt-4 max-w-md text-white/80">Hubungi tim kami untuk informasi jadwal dan paket perjalanan.</motion.p>
      <motion.div {...fadeUp} className="mt-8 flex flex-wrap gap-3">
        <a href="https://instagram.com/arumichitravel" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-medium text-white hover:bg-brand-dark transition-colors" data-testid={LANDING.ctaInstagram}><Instagram className="h-4 w-4" /> Instagram</a>
        <a href="mailto:cs@arumichitravel.com" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-slate-900 hover:bg-sky-50 transition-colors" data-testid={LANDING.ctaEmail}><Mail className="h-4 w-4" /> Kirim Email</a>
      </motion.div>
    </div>
  </ParallaxSection>
);

export default function Landing() {
  return (
    <div className="bg-white" data-testid={LANDING.page}>
      <Hero />
      <Services />
      <Quote />
      <Contact />
      <LandingFooter />
    </div>
  );
}
